import { useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Building2,
  Landmark,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  UserRound,
  Users,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { db, type CachedSupplier } from "../lib/db";
import { refreshFromServer } from "../lib/data";
import { Card, EmptyState, InlineNotice, PageHeader, StatusBadge } from "../components/ui";

type Tab = "CUSTOMER" | "SUPPLIER";

interface SupplierForm {
  name: string;
  legalName: string;
  phone: string;
  email: string;
  address: string;
  taxCode: string;
  bankName: string;
  bankAccountNumber: string;
  representativeName: string;
  representativePhone: string;
  note: string;
}

const EMPTY_SUPPLIER: SupplierForm = {
  name: "",
  legalName: "",
  phone: "",
  email: "",
  address: "",
  taxCode: "",
  bankName: "",
  bankAccountNumber: "",
  representativeName: "",
  representativePhone: "",
  note: "",
};

function supplierToForm(supplier: CachedSupplier): SupplierForm {
  return {
    name: supplier.name ?? "",
    legalName: supplier.legalName ?? "",
    phone: supplier.phone ?? "",
    email: supplier.email ?? "",
    address: supplier.address ?? "",
    taxCode: supplier.taxCode ?? "",
    bankName: supplier.bankName ?? "",
    bankAccountNumber: supplier.bankAccountNumber ?? "",
    representativeName: supplier.representativeName ?? "",
    representativePhone: supplier.representativePhone ?? "",
    note: supplier.note ?? "",
  };
}

function cleanSupplierPayload(form: SupplierForm) {
  const nullable = (value: string) => value.trim() || null;
  return {
    name: form.name.trim(),
    legalName: nullable(form.legalName),
    phone: nullable(form.phone),
    email: nullable(form.email),
    address: nullable(form.address),
    taxCode: nullable(form.taxCode),
    bankName: nullable(form.bankName),
    bankAccountNumber: nullable(form.bankAccountNumber),
    representativeName: nullable(form.representativeName),
    representativePhone: nullable(form.representativePhone),
    note: nullable(form.note),
  };
}

export function PartnersPage() {
  const auth = useAuth();
  const customers = useLiveQuery(() => db.customers.orderBy("name").toArray(), [], []);
  const suppliers = useLiveQuery(() => db.suppliers.orderBy("name").toArray(), [], []);

  const [tab, setTab] = useState<Tab>("CUSTOMER");
  const [showForm, setShowForm] = useState(false);
  const [editingSupplierId, setEditingSupplierId] = useState<string | null>(null);

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [supplierForm, setSupplierForm] = useState<SupplierForm>(EMPTY_SUPPLIER);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const role = auth.session?.user.role ?? "OWNER";
  const canWrite =
    auth.demo ||
    (navigator.onLine &&
      ["OWNER", "MANAGER", "STAFF"].includes(role));

  function openCreate() {
    setError("");
    setEditingSupplierId(null);
    setCustomerName("");
    setCustomerPhone("");
    setSupplierForm(EMPTY_SUPPLIER);
    setShowForm(true);
  }

  function openEditSupplier(supplier: CachedSupplier) {
    setError("");
    setEditingSupplierId(supplier.id);
    setSupplierForm(supplierToForm(supplier));
    setShowForm(true);
  }

  function patchSupplier<K extends keyof SupplierForm>(
    key: K,
    value: SupplierForm[K],
  ) {
    setSupplierForm((current) => ({ ...current, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!canWrite) return;

    const supplierMode = tab === "SUPPLIER";
    if (supplierMode && !supplierForm.name.trim()) return;
    if (!supplierMode && !customerName.trim()) return;

    setBusy(true);
    setError("");

    try {
      if (supplierMode) {
        const payload = cleanSupplierPayload(supplierForm);

        if (auth.demo) {
          if (editingSupplierId) {
            await db.suppliers.update(editingSupplierId, payload);
          } else {
            await db.suppliers.add({
              id: crypto.randomUUID(),
              ...payload,
            });
          }
        } else {
          await api(
            editingSupplierId
              ? `/suppliers/${editingSupplierId}`
              : "/suppliers",
            {
              method: editingSupplierId ? "PATCH" : "POST",
              body: JSON.stringify(payload),
            },
          );
          await refreshFromServer();
        }
      } else {
        const payload = {
          name: customerName.trim(),
          phone: customerPhone.trim() || null,
        };

        if (auth.demo) {
          await db.customers.add({
            id: crypto.randomUUID(),
            ...payload,
          });
        } else {
          await api("/customers", {
            method: "POST",
            body: JSON.stringify(payload),
          });
          await refreshFromServer();
        }
      }

      setShowForm(false);
      setEditingSupplierId(null);
      setCustomerName("");
      setCustomerPhone("");
      setSupplierForm(EMPTY_SUPPLIER);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Không thể lưu đối tác",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-stack">
      <PageHeader
        back
        title="Khách hàng & nhà cung cấp"
        description="Quản lý hồ sơ đối tác phục vụ bán hàng, nhập hàng, công nợ và đối chiếu chứng từ"
        action={
          <button
            className="btn btn-primary"
            onClick={openCreate}
            disabled={!canWrite}
          >
            <Plus size={18} /> Thêm
          </button>
        }
      />

      {!canWrite ? (
        <InlineNotice tone="warning" title="Đang ngoại tuyến">
          Tạo hoặc sửa đối tác cần kết nối để tránh trùng dữ liệu giữa các thiết bị.
        </InlineNotice>
      ) : null}
      {error ? <InlineNotice tone="danger" title={error} /> : null}

      <div className="segmented">
        <button
          className={tab === "CUSTOMER" ? "active" : ""}
          onClick={() => {
            setTab("CUSTOMER");
            setShowForm(false);
          }}
        >
          Khách hàng
        </button>
        <button
          className={tab === "SUPPLIER" ? "active" : ""}
          onClick={() => {
            setTab("SUPPLIER");
            setShowForm(false);
          }}
        >
          Nhà cung cấp
        </button>
      </div>

      {tab === "CUSTOMER" ? (
        customers.length === 0 ? (
          <EmptyState
            title="Chưa có khách hàng"
            description="Bán hàng bình thường không bắt buộc tạo khách."
            action={
              canWrite ? (
                <button className="btn btn-primary" onClick={openCreate}>
                  <Plus size={18} /> Thêm mới
                </button>
              ) : undefined
            }
          />
        ) : (
          <Card className="partner-list">
            {customers.map((item) => (
              <div className="partner-row" key={item.id}>
                <span className="partner-icon">
                  <UserRound size={20} />
                </span>
                <div>
                  <strong>{item.name}</strong>
                  <small>{item.phone || "Chưa có số điện thoại"}</small>
                </div>
              </div>
            ))}
          </Card>
        )
      ) : suppliers.length === 0 ? (
        <EmptyState
          title="Chưa có nhà cung cấp"
          description="Tạo hồ sơ nhà cung cấp trước khi nhập hàng hoặc theo dõi công nợ."
          action={
            canWrite ? (
              <button className="btn btn-primary" onClick={openCreate}>
                <Plus size={18} /> Thêm nhà cung cấp
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="page-stack">
          {suppliers.map((supplier) => (
            <Card key={supplier.id}>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", gap: 12, minWidth: 0 }}>
                  <span className="partner-icon">
                    <Building2 size={20} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <strong>{supplier.name}</strong>
                    {supplier.legalName && supplier.legalName !== supplier.name ? (
                      <small style={{ display: "block", marginTop: 2 }}>
                        {supplier.legalName}
                      </small>
                    ) : null}
                    <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {supplier.taxCode ? (
                        <StatusBadge tone="info">MST {supplier.taxCode}</StatusBadge>
                      ) : null}
                      {supplier.bankAccountNumber ? (
                        <StatusBadge tone="neutral">Đã có STK ngân hàng</StatusBadge>
                      ) : null}
                    </div>
                  </div>
                </div>

                {canWrite ? (
                  <button
                    type="button"
                    className="btn btn-secondary compact"
                    onClick={() => openEditSupplier(supplier)}
                  >
                    <Pencil size={16} /> Sửa
                  </button>
                ) : null}
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  marginTop: 16,
                }}
              >
                <div>
                  <small>Liên hệ công ty</small>
                  <div style={{ display: "grid", gap: 4, marginTop: 4 }}>
                    <span><Phone size={14} style={{ marginRight: 6 }} />{supplier.phone || "Chưa có SĐT"}</span>
                    <span><Mail size={14} style={{ marginRight: 6 }} />{supplier.email || "Chưa có email"}</span>
                  </div>
                </div>

                <div>
                  <small>Địa chỉ</small>
                  <div style={{ marginTop: 4 }}>
                    <MapPin size={14} style={{ marginRight: 6 }} />
                    {supplier.address || "Chưa có địa chỉ"}
                  </div>
                </div>

                <div>
                  <small>Tài khoản ngân hàng</small>
                  <div style={{ display: "grid", gap: 4, marginTop: 4 }}>
                    <span><Landmark size={14} style={{ marginRight: 6 }} />{supplier.bankName || "Chưa có tên ngân hàng"}</span>
                    <strong>{supplier.bankAccountNumber || "Chưa có số tài khoản"}</strong>
                  </div>
                </div>

                <div>
                  <small>Người đại diện / liên hệ</small>
                  <div style={{ display: "grid", gap: 4, marginTop: 4 }}>
                    <span><UserRound size={14} style={{ marginRight: 6 }} />{supplier.representativeName || "Chưa có người liên hệ"}</span>
                    <span><Phone size={14} style={{ marginRight: 6 }} />{supplier.representativePhone || "Chưa có SĐT người liên hệ"}</span>
                  </div>
                </div>
              </div>

              {supplier.note ? (
                <div style={{ marginTop: 12 }}>
                  <small>Ghi chú: {supplier.note}</small>
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}

      {showForm ? (
        <div
          className="modal-backdrop"
          onMouseDown={() => !busy && setShowForm(false)}
        >
          <form
            className="bottom-sheet"
            onSubmit={submit}
            onMouseDown={(event) => event.stopPropagation()}
            style={{ maxHeight: "90vh", overflowY: "auto" }}
          >
            <div className="sheet-handle" />
            <div className="form-section-title">
              <Users size={20} />
              <h2>
                {tab === "CUSTOMER"
                  ? "Thêm khách hàng"
                  : editingSupplierId
                    ? "Cập nhật nhà cung cấp"
                    : "Thêm nhà cung cấp"}
              </h2>
            </div>

            {tab === "CUSTOMER" ? (
              <>
                <label>
                  Tên
                  <input
                    value={customerName}
                    onChange={(event) => setCustomerName(event.target.value)}
                    autoFocus
                    required
                  />
                </label>
                <label>
                  Số điện thoại
                  <input
                    inputMode="tel"
                    value={customerPhone}
                    onChange={(event) => setCustomerPhone(event.target.value)}
                  />
                </label>
              </>
            ) : (
              <>
                <div className="form-section-title"><h2>Thông tin công ty</h2></div>
                <label>
                  Tên công ty / nhà cung cấp *
                  <input
                    value={supplierForm.name}
                    onChange={(event) => patchSupplier("name", event.target.value)}
                    autoFocus
                    required
                  />
                </label>
                <label>
                  Tên pháp lý / trên hóa đơn
                  <input
                    value={supplierForm.legalName}
                    onChange={(event) => patchSupplier("legalName", event.target.value)}
                  />
                </label>
                <div className="form-grid">
                  <label>
                    Số điện thoại
                    <input
                      inputMode="tel"
                      value={supplierForm.phone}
                      onChange={(event) => patchSupplier("phone", event.target.value)}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={supplierForm.email}
                      onChange={(event) => patchSupplier("email", event.target.value)}
                    />
                  </label>
                  <label>
                    Mã số thuế
                    <input
                      value={supplierForm.taxCode}
                      onChange={(event) => patchSupplier("taxCode", event.target.value)}
                    />
                  </label>
                </div>
                <label>
                  Địa chỉ
                  <textarea
                    rows={2}
                    value={supplierForm.address}
                    onChange={(event) => patchSupplier("address", event.target.value)}
                  />
                </label>

                <div className="form-section-title"><h2>Ngân hàng</h2></div>
                <div className="form-grid">
                  <label>
                    Tên ngân hàng
                    <input
                      value={supplierForm.bankName}
                      onChange={(event) => patchSupplier("bankName", event.target.value)}
                    />
                  </label>
                  <label>
                    Số tài khoản ngân hàng
                    <input
                      inputMode="numeric"
                      value={supplierForm.bankAccountNumber}
                      onChange={(event) => patchSupplier("bankAccountNumber", event.target.value)}
                    />
                  </label>
                </div>

                <div className="form-section-title"><h2>Người đại diện / liên hệ</h2></div>
                <div className="form-grid">
                  <label>
                    Họ tên người đại diện
                    <input
                      value={supplierForm.representativeName}
                      onChange={(event) => patchSupplier("representativeName", event.target.value)}
                    />
                  </label>
                  <label>
                    Số điện thoại người đại diện
                    <input
                      inputMode="tel"
                      value={supplierForm.representativePhone}
                      onChange={(event) => patchSupplier("representativePhone", event.target.value)}
                    />
                  </label>
                </div>

                <label>
                  Ghi chú
                  <textarea
                    rows={2}
                    value={supplierForm.note}
                    onChange={(event) => patchSupplier("note", event.target.value)}
                  />
                </label>
              </>
            )}

            <button
              className="btn btn-primary btn-lg"
              disabled={
                busy ||
                (tab === "CUSTOMER"
                  ? !customerName.trim()
                  : !supplierForm.name.trim())
              }
            >
              {busy
                ? "Đang lưu..."
                : editingSupplierId
                  ? "Lưu thay đổi"
                  : "Lưu"}
            </button>
          </form>
        </div>
      ) : null}
    </div>
  );
}
